import React, { useContext, useState, useEffect, useRef } from "react";

import GameRoomVoiceChat from "./GameRoomVoiceChat";

import { useSound } from "../../utils/sound/SoundProvider";

import UserContext from "../../context/UserContext";
import SocketContext from "../../context/SocketContext";
import GameRoomContext from "../../context/GameRoomContext";

import GameRoomConfig from "./GameRoomConfig";
import GameRoomChat from "./GameRoomChat";
import GameRoomPlayers from "./GameRoomPlayers";
import GameRoomPlayAgain from "./GameRoomPlayAgain";
import GameRoomHeading from "./GameRoomHeading";
import GameRoomSpectators from "./GameRoomSpectators";
import Modal from "../common/Modal";
import { FLAG_LABELS } from "../../utils/flagLabels";

import UIContext from "../../context/UIContext";
import GAME_COMPONENTS from "../games";
import { pidOf, currentClientId, isLocalPlayerId } from "../../utils/normalizePlayerId";

function GameRoom() {
  const socket = useContext(SocketContext);
  const { user } = useContext(UserContext);
  const { gameTypes, addToast } = useContext(UIContext);
  const {
    gameRoomData,
    leaveRoom,
    roomMessages,
    gameRoomTypingPlayers,
    handleGameRoomChatInputChange,
    handleGameRoomChatSend,
    onGameAction,
    onChangeGameType,
    onRoomParticipantAction,
    sendMessage,
    onToggleAllowSpectators,
  } = useContext(GameRoomContext);

  // Determine if current user is a player or spectator
  const myId = currentClientId(socket, user);
  const isSpectator = (gameRoomData.gameRoomSpectators || []).some(
    (s) => pidOf(s) === myId
  );

  const gameState = React.useMemo(
    () => gameRoomData.gameRoomGame?.gameState || {},
    [gameRoomData?.gameRoomGame?.gameState]
  );
  const roomId = gameRoomData.gameRoomDetails?.roomId || "";
  const roomName = gameRoomData?.gameRoomDetails?.roomName || "";
  
  const isHost = myId && (myId === gameRoomData.gameRoomDetails?.hostUserId || myId === gameRoomData.gameRoomDetails?.hostSocketId);

  // Add state for password input and modal
  const [passwordInput, setPasswordInput] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [discordLinkInput, setDiscordLinkInput] = useState("");
  const [showDiscordModal, setShowDiscordModal] = useState(false);
  const [showDisableSpectatorsConfirm, setShowDisableSpectatorsConfirm] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [isEditingRoomName, setIsEditingRoomName] = useState(false);
  const [draftRoomName, setDraftRoomName] = useState("");
  const roomNameInputRef = useRef(null);

  const gamePlayers = React.useMemo(
    () => gameRoomData.gameRoomPlayers || [],
    [gameRoomData?.gameRoomPlayers]
  );
  const configSchema = gameRoomData.gameRoomConfigSchema || {};
  // gameConfig intentionally unused here (kept in data for nested components)
  // prefer context-managed typing players if provided
  // const gameRoomTypingPlayers = gameRoomData.gameRoomTypingPlayers || [];
  const messages = React.useMemo(
    () => roomMessages || gameRoomData.messages || [],
    [roomMessages, gameRoomData?.messages]
  );
  const allowSpectators = gameRoomData?.gameRoomDetails?.allowSpectators !== false;
  const discordInviteUrl = gameRoomData?.gameRoomDetails?.discordInviteUrl || null;
  const spectatorCount = gameRoomData?.gameRoomSpectators?.length || 0;
  const maxPlayers = configSchema?.maxPlayers ?? Infinity;
  const nextRoundPendingPlayers = gameRoomData?.gameRoomDetails?.nextRoundPendingPlayers || [];
  const nextRoundSlotsRemaining = gameRoomData?.gameRoomDetails?.nextRoundSlotsRemaining ?? 0;
  const isQueuedForNextRound = !!(myId && nextRoundPendingPlayers.includes(myId));
  const sound = useSound();

  // Voice chat user/speaking state (global, updated by socket events)
  const [voiceChatUsers, setVoiceChatUsers] = useState([]); // array of socketIds
  const [speakingUsers, setSpeakingUsers] = useState([]); // array of socketIds
  const [isChatVisible, setIsChatVisible] = useState(false);

  // Pulsate animation for turn indication (top edge only)

  // Listen for global voice chat events
  useEffect(() => {
    if (!socket) return;
      const handleVoiceJoin = ({ id }) => {
        setVoiceChatUsers((prev) => {
          const next = Array.from(new Set([...prev, id]));
          // Only play sound if this join makes the list go from empty to 1
          if (prev.length === 0 && next.length === 1) {
            try {
              sound && sound.play && sound.play("ui:voicechatstarted");
            } catch (e) {}
          }
          return next;
        });
      };
    const handleVoiceLeave = ({ id }) => {
      setVoiceChatUsers((prev) => prev.filter((uid) => uid !== id));
      setSpeakingUsers((prev) => prev.filter((uid) => uid !== id));
    };
    const handleVoiceSpeaking = ({ id, speaking }) => {
      setSpeakingUsers((prev) =>
        speaking
          ? Array.from(new Set([...prev, id]))
          : prev.filter((uid) => uid !== id)
      );
    };
    socket.on("voice-join", handleVoiceJoin);
    socket.on("voice-leave", handleVoiceLeave);
    socket.on("voice-speaking", handleVoiceSpeaking);
    return () => {
      socket.off("voice-join", handleVoiceJoin);
      socket.off("voice-leave", handleVoiceLeave);
      socket.off("voice-speaking", handleVoiceSpeaking);
    };
  }, [socket, sound]);

  const handleGameRoomTyping = (isTyping) => {
    if (socket && roomId) {
      socket.emit("gameRoomTyping", {
        roomId,
        username: user.username,
        isTyping,
        context: "room",
      });
    }
  };

  const [chatInput, setChatInput] = React.useState("");
  const typingTimeout = React.useRef(null);

  const [showConfirm, setShowConfirm] = React.useState(false);
  const handleLeaveClick = (e) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const confirmLeave = () => {
    setShowConfirm(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
    leaveRoom();
  };

  const cancelLeave = () => {
    setShowConfirm(false);
  };

  // Play chat receive sound for incoming messages from other users
  const prevMessagesLenRef = useRef((messages || []).length);
  useEffect(() => {
    try {
      const prevLen = prevMessagesLenRef.current || 0;
      const curLen = (messages || []).length;
      if (curLen > prevLen) {
        // Get the newly added messages
        const newMsgs = (messages || []).slice(prevLen);
        for (const m of newMsgs) {
          // If message has a userId/username and it's not the current user, play sound
          const fromUserId = m.userId || m.fromUserId || null;
          const fromUsername = m.username || m.from || null;
          const isFromMe =
            (fromUserId && fromUserId === user?.userId) ||
            (fromUsername && fromUsername === user?.username);
          const isChatMessage = !m.type || m.type === "chat";
          if (!isFromMe && isChatMessage) {
            try {
              sound && sound.play && sound.play("ui:chatreceived");
            } catch (e) {}
            try {
              const who = fromUsername || "Someone";
              const text = typeof m.text === "string" ? m.text.trim() : "";
              if (text && !isChatVisible && typeof addToast === "function") {
                addToast(text, {
                  duration: 2000,
                  type: "chat",
                  username: who,
                  icon: m.icon,
                  color: m.color,
                });
              }
            } catch (e) {}
          }
        }
      }
      prevMessagesLenRef.current = curLen;
    } catch (e) {}
  }, [messages, sound, user, addToast, isChatVisible]);

  // Notify everyone in the room when a Discord chat link becomes available
  const prevDiscordInviteUrlRef = useRef(discordInviteUrl);
  useEffect(() => {
    const prevUrl = prevDiscordInviteUrlRef.current;
    if (!prevUrl && discordInviteUrl && typeof addToast === "function") {
      addToast("Discord chat enabled for this room — join it with the Discord button!", {
        type: "success",
        username: "Discord Chat",
        icon: "🎙️",
        color: "#c7d2fe",
        duration: 4000,
      });
    }
    prevDiscordInviteUrlRef.current = discordInviteUrl;
  }, [discordInviteUrl, addToast]);

  const handleSend = (e) => {
    e.preventDefault();
    if (chatInput.trim()) {
      if (handleGameRoomChatSend) handleGameRoomChatSend(chatInput.trim());
      setChatInput("");
      try {
        if (handleGameRoomChatInputChange)
          handleGameRoomChatInputChange({
            username: user.username,
            isTyping: false,
          });
      } catch (e) {}
    }
  };

  const handleInputChange = (e) => {
    setChatInput(e.target.value);
    try {
      if (handleGameRoomChatInputChange)
        handleGameRoomChatInputChange({
          username: user.username,
          isTyping: true,
        });
    } catch (e) {}
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      try {
        if (handleGameRoomChatInputChange)
          handleGameRoomChatInputChange({
            username: user.username,
            isTyping: false,
          });
      } catch (e) {}
    }, 1200);
  };

  const handleInputBlur = () => {
    // Clear typing immediately when input loses focus
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
      typingTimeout.current = null;
    }
    try {
      if (handleGameRoomChatInputChange)
        handleGameRoomChatInputChange({
          username: user.username,
          isTyping: false,
        });
    } catch (e) {}
  };

  let gameComponent = "";
  const gameAreaRef = useRef(null);
  const turnOverlayTimeoutRef = useRef(null);
  const [showTurnOverlay, setShowTurnOverlay] = useState(false);

  // Countdown overlay state
  const [countdownValue, setCountdownValue] = useState(3);
  // Keep a ref copy of countdownValue so interval callbacks can read the latest value without
  // depending on the state closure.
  const countdownValueRef = useRef(countdownValue);

  useEffect(() => {
    countdownValueRef.current = countdownValue;
  }, [countdownValue]);

  // Compute the boolean shouldStart outside the effect and depend only on it so
  // we don't re-run the effect when large objects change identity frequently.
  const countdownToStart = gameState?.countdownToStart;
  const isTurnBased = gameState?.isTurnBased;
  const gameClass = gameRoomData?.gameRoomConfigSchema?.gameClass;
  const usesEmbeddedCountdownOverlay = gameClass === 'SlotCars';
  const serverCountdownRaw = gameRoomData?.gameRoomGame?.countdownToStart;
  const serverCountdownActive = !!serverCountdownRaw;
  const shouldStart = !!(
    gameState &&
    gameClass &&
    !usesEmbeddedCountdownOverlay &&
    isTurnBased === false &&
    (countdownToStart || serverCountdownActive)
  );

  // Expose these values through refs so the watchdog can log them without
  // requiring them in the effect dependency array (avoids frequent effect restarts).
  const countdownToStartRefValue = useRef(countdownToStart);
  const serverCountdownRawRef = useRef(serverCountdownRaw);
  countdownToStartRefValue.current = countdownToStart;
  serverCountdownRawRef.current = serverCountdownRaw;

  // Improved countdown: use an end-timestamp + interval to compute remaining seconds.
  // This is more robust against timer throttling, re-renders, and StrictMode double-invokes.
  const countdownIntervalRef = useRef(null);
  const countdownEndRef = useRef(null);
  useEffect(() => {
    // derive numeric duration (prefer server wrapper value if present)
    const durationSeconds = Number(serverCountdownRaw ?? countdownToStart ?? 3) || 3;

    const clearIntervalRef = () => {
      try {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      } catch (e) {
        /* ignore */
      }
    };

    if (shouldStart) {
      // compute canonical end timestamp
      countdownEndRef.current = Date.now() + durationSeconds * 1000;
      // set initial visible value (ceil so we show whole seconds: 3,2,1,...)
      const initial = Math.ceil((countdownEndRef.current - Date.now()) / 1000);
      setCountdownValue(initial);
      countdownValueRef.current = initial;

      // start an interval to update remaining seconds; small interval gives snappy updates
      clearIntervalRef();
      countdownIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const rem = Math.ceil((countdownEndRef.current - now) / 1000);
        if (rem <= 0) {
          // reached 0
          setCountdownValue(0);
          countdownValueRef.current = 0;
          clearIntervalRef();
        } else {
          setCountdownValue(rem);
          countdownValueRef.current = rem;
        }
      }, 150);
    } else {
      // not running: ensure interval cleared and state reset
      clearIntervalRef();
      countdownEndRef.current = null;
      setCountdownValue(3);
      countdownValueRef.current = 3;
    }

    return () => {
      clearIntervalRef();
      countdownEndRef.current = null;
      setCountdownValue(3);
      countdownValueRef.current = 3;
    };
  }, [shouldStart, serverCountdownRaw, countdownToStart]);

  // Emit 'countdown-finished' when countdownValue transitions to 0 (GO!)
  useEffect(() => {
    if (
      gameState &&
      gameState.countdownToStart &&
      countdownValue === 0 &&
      socket &&
      roomId
    ) {
      socket.emit("countdown-finished", { roomId });
    }
  }, [countdownValue, gameState, socket, roomId]);

  // PHASE BOOLEANS
  const gameInConfigPhase = !gameState?.started;
  // Use server state for countdown phase, not local showCountdown

  // Server-wrapper flags (may be undefined, numeric >0, or boolean). Normalize to booleans where appropriate.
  // `serverCountdownRaw` and `serverCountdownActive` are computed above near the
  // countdown effect so reuse those values here (avoid redeclaring block-scoped vars).
  const serverFinished = gameRoomData?.gameRoomGame?.finished;
  const serverStarted = gameRoomData?.gameRoomGame?.started;
  const isTransientGuessReveal = gameState?.gameType === 'FaceFinder' && gameState?.phase === 'guess-reveal';
  // Use server-provided countdown flag so client phase logic is consistent with server wrapper
  // Consider either the inner gameState countdown or the server wrapper countdown as active.
  const clientCountdownActive = !!(
    gameState &&
    gameState.countdownToStart > 0 &&
    !usesEmbeddedCountdownOverlay &&
    !gameState.isTurnBased
  );
  const gameInCountdownPhase = clientCountdownActive || serverCountdownActive;
  const gameInStartedPhase =
    serverStarted && !serverCountdownActive && (!serverFinished || isTransientGuessReveal);
  // Prefer the in-state flag, but fall back to the server wrapper finished flag if present
  const gameInFinishedPhase = !!((gameState?.finished || serverFinished) && !isTransientGuessReveal);
  const isConfigVisible = gameInConfigPhase && !gameInCountdownPhase;

  useEffect(() => {
    if (!selectedParticipant) return;
    const selectedId = pidOf(selectedParticipant);
    if (!selectedId) {
      setSelectedParticipant(null);
      return;
    }

    const nextPlayer = (gameRoomData?.gameRoomPlayers || []).find((participant) => pidOf(participant) === selectedId);
    if (nextPlayer) {
      if (
        selectedParticipant.role !== "player" ||
        selectedParticipant.username !== nextPlayer.username ||
        selectedParticipant.flag !== nextPlayer.flag ||
        selectedParticipant.socketId !== nextPlayer.socketId
      ) {
        setSelectedParticipant({ ...nextPlayer, role: "player" });
      }
      return;
    }

    const nextSpectator = (gameRoomData?.gameRoomSpectators || []).find((participant) => pidOf(participant) === selectedId);
    if (nextSpectator) {
      if (
        selectedParticipant.role !== "spectator" ||
        selectedParticipant.username !== nextSpectator.username ||
        selectedParticipant.flag !== nextSpectator.flag ||
        selectedParticipant.socketId !== nextSpectator.socketId
      ) {
        setSelectedParticipant({ ...nextSpectator, role: "spectator" });
      }
      return;
    }

    setSelectedParticipant(null);
  }, [gameRoomData?.gameRoomPlayers, gameRoomData?.gameRoomSpectators, selectedParticipant]);

  const selectedParticipantId = selectedParticipant ? pidOf(selectedParticipant) : null;
  const selectedParticipantIsHost = !!(
    selectedParticipant && (
      selectedParticipant.isHost ||
      selectedParticipantId === gameRoomData?.gameRoomDetails?.hostUserId ||
      selectedParticipantId === gameRoomData?.gameRoomDetails?.hostSocketId
    )
  );
  const selectedParticipantIsCurrentUser = !!(selectedParticipantId && selectedParticipantId === myId);
  const canHostManageSelectedParticipant = !!(
    isHost &&
    selectedParticipant &&
    !selectedParticipantIsHost &&
    !selectedParticipantIsCurrentUser
  );

  const canMakeSelectedParticipantHost = !!(
    isHost &&
    selectedParticipant &&
    !selectedParticipantIsHost &&
    selectedParticipant.role === "player" &&
    !selectedParticipantIsCurrentUser
  );
  const canKickSelectedParticipant = canHostManageSelectedParticipant;
  const canMoveSelectedParticipantToSpectator = !!(
    canHostManageSelectedParticipant &&
    selectedParticipant?.role === "player" &&
    allowSpectators &&
    isConfigVisible
  );
  const canMoveSelectedParticipantToPlayer = !!(
    canHostManageSelectedParticipant &&
    selectedParticipant?.role === "spectator" &&
    isConfigVisible &&
    gamePlayers.length < maxPlayers
  );

  const handleParticipantAction = (action) => {
    if (!roomId || !selectedParticipantId) return;
    if (action === "makePlayer") {
      onRoomParticipantAction && onRoomParticipantAction(roomId, selectedParticipantId, "makePlayer");
    } else if (action === "makeSpectator") {
      onRoomParticipantAction && onRoomParticipantAction(roomId, selectedParticipantId, "makeSpectator");
    } else if (action === "kick") {
      onRoomParticipantAction && onRoomParticipantAction(roomId, selectedParticipantId, "kick");
    } else if (action === "makeHost") {
      onRoomParticipantAction && onRoomParticipantAction(roomId, selectedParticipantId, "makeHost");
    }
    setSelectedParticipant(null);
  };

  const handleToggleSpectatorsClick = () => {
    if (!onToggleAllowSpectators || !roomId) return;

    if (allowSpectators && spectatorCount > 0) {
      setShowDisableSpectatorsConfirm(true);
      return;
    }

    onToggleAllowSpectators(roomId, !allowSpectators);
  };

  const confirmDisableSpectators = () => {
    if (onToggleAllowSpectators && roomId) {
      onToggleAllowSpectators(roomId, false);
    }
    setShowDisableSpectatorsConfirm(false);
  };

  const cancelDisableSpectators = () => {
    setShowDisableSpectatorsConfirm(false);
  };

  useEffect(() => {
    if (!isEditingRoomName) {
      setDraftRoomName(roomName);
    }
  }, [roomName, isEditingRoomName]);

  useEffect(() => {
    if (!isEditingRoomName || !roomNameInputRef.current) return;
    roomNameInputRef.current.focus();
    roomNameInputRef.current.select();
  }, [isEditingRoomName]);

  const beginRoomNameEdit = () => {
    if (!isHost || !roomId) return;
    setDraftRoomName(roomName);
    setIsEditingRoomName(true);
  };

  const cancelRoomNameEdit = () => {
    setDraftRoomName(roomName);
    setIsEditingRoomName(false);
  };

  const saveRoomNameEdit = () => {
    if (!isHost || !roomId) return;
    const nextRoomName = String(draftRoomName || "").trim();

    if (!nextRoomName) {
      if (typeof addToast === "function") {
        addToast("Room name cannot be empty", { type: "error", duration: 2200 });
      }
      cancelRoomNameEdit();
      return;
    }

    if (nextRoomName.length > 20) {
      if (typeof addToast === "function") {
        addToast("Room name too long (max 20 characters)", { type: "error", duration: 2200 });
      }
      setDraftRoomName(nextRoomName.slice(0, 20));
      return;
    }

    setIsEditingRoomName(false);

    if (nextRoomName === roomName) {
      setDraftRoomName(nextRoomName);
      return;
    }

    socket.emit("setRoomName", { roomId, roomName: nextRoomName }, (response) => {
      if (response?.ok) {
        setDraftRoomName(response.roomName || nextRoomName);
        return;
      }

      setDraftRoomName(roomName);
      if (typeof addToast === "function") {
        addToast(response?.error || "Failed to update room name", { type: "error", duration: 2500 });
      }
    });
  };

  // console.log("gameInStartedPhase - " + gameInStartedPhase);
  // console.log("gameInFinishedPhase - " + gameInFinishedPhase);
  // console.log("gameInConfigPhase - " + gameInConfigPhase);
  //console.log("gameInCountdownPhase - " + gameInCountdownPhase);
  // console.log("Combined:", gameInStartedPhase && !gameInConfigPhase && !gameInCountdownPhase  );

  // Vibrate on turn change (mobile)
  const prevIsMyTurnRef = React.useRef(false);
  useEffect(() => {
    // Normalize player ids so we correctly detect the local player's turn
    // regardless of whether server uses socketId or a persistent userId.
    const currentPlayerId = gameState?.currentPlayerSocketId || gameState?.currentPlayer;
    const isMyTurn =
      gameState?.isTurnBased &&
      gameInStartedPhase &&
      !gameState?.finished &&
      isLocalPlayerId(currentPlayerId, gamePlayers, socket, user);
    if (isMyTurn && !prevIsMyTurnRef.current) {
      setShowTurnOverlay(true);
      if (turnOverlayTimeoutRef.current) {
        clearTimeout(turnOverlayTimeoutRef.current);
      }
      turnOverlayTimeoutRef.current = setTimeout(() => {
        setShowTurnOverlay(false);
      }, 2000);

      try {
        sound && sound.play && sound.play("ui:yourturn");
      } catch (e) {}

      // if (gameAreaRef.current && typeof gameAreaRef.current.scrollIntoView === "function") {
      //   requestAnimationFrame(() => {
      //     try {
      //       gameAreaRef.current.scrollIntoView({
      //         behavior: "smooth",
      //         block: "center",
      //         inline: "nearest",
      //       });
      //     } catch (e) {
      //       gameAreaRef.current.scrollIntoView();
      //     }
      //   });
      // }

      if (
        typeof window !== "undefined" &&
        window.navigator &&
        typeof window.navigator.vibrate === "function"
      ) {
        window.navigator.vibrate([120, 60, 180]);
      }
    }
    if (!isMyTurn) {
      setShowTurnOverlay(false);
      if (turnOverlayTimeoutRef.current) {
        clearTimeout(turnOverlayTimeoutRef.current);
        turnOverlayTimeoutRef.current = null;
      }
    }
    prevIsMyTurnRef.current = isMyTurn;
  }, [
    gameState?.currentPlayer,
    gameState?.currentPlayerSocketId,
    gameState?.isTurnBased,
    gameInStartedPhase,
    gameState?.finished,
    gamePlayers,
    socket,
    user,
    sound,
  ]);

  useEffect(() => {
    return () => {
      if (turnOverlayTimeoutRef.current) {
        clearTimeout(turnOverlayTimeoutRef.current);
      }
    };
  }, []);

  
  const GameComponent =
    GAME_COMPONENTS[gameRoomData?.gameRoomConfigSchema?.gameClass];
  // if (gameInStartedPhase && GameComponent) {
  if (GameComponent) {
    gameComponent = (
      <GameComponent
        gameState={gameState}
        gamePlayers={gamePlayers}
        user={user}
        isHost={isHost}
        isSpectator={isSpectator}
        onGameAction={onGameAction}
        socket={socket}
        roomId={roomId}
      />
    );
  }
  
  // Show play again when either the game state or server wrapper indicates finished
  const showPlayAgain = serverFinished && !isTransientGuessReveal;

  // Transient banner for server-provided human-readable last action messages
  const [lastActionBanner, setLastActionBanner] = useState(null);
  useEffect(() => {
    const msg = gameRoomData?.gameRoomGame?.lastActionMessage;
    if (!msg) return;
    setLastActionBanner(msg);
    const t = setTimeout(() => setLastActionBanner(null), 4000);
    return () => clearTimeout(t);
  }, [gameRoomData?.gameRoomGame?.lastActionMessage]);

  // reference lastActionBanner so linters don't mark it as unused (UI block is currently commented)
  useEffect(() => {}, [lastActionBanner]);

  return (
    <>

      <style>{`
        @keyframes discordLinkPulse {
          0% { box-shadow: 0 0 0 0 rgba(99,102,241,0.55); }
          70% { box-shadow: 0 0 0 9px rgba(99,102,241,0); }
          100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); }
        }
        .discord-link-pulse { animation: discordLinkPulse 2.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .discord-link-pulse { animation: none; }
        }
      `}</style>

      {showTurnOverlay && (
        <div className="pointer-events-none fixed inset-x-0 top-4 z-[70] flex justify-center px-4">
          <div className="rounded-full border border-amber-200 bg-amber-300/90 px-6 py-3 text-xl font-black uppercase tracking-[0.18em] text-black shadow-[0_18px_45px_rgba(0,0,0,0.28)] sm:px-8 sm:py-4 sm:text-2xl">
            Your Turn
          </div>
        </div>
      )}

      {/* Main Content - match width to title pane */}

      <div className="flex-1 min-w-0 mb-20 sm:mb-20">
        {/* Inject pulsate animation style */}

        {/* <main className="flex flex-col md:flex-row gap-16 md:gap-8 px-2 py-6"> */}
        {/* Left: GameConfig/GameContent */}
        {/* <section className="w-full md:w-2/3 flex flex-col gap-6"> */}
        <div className="relative" ref={gameAreaRef}>

          <GameRoomHeading
            gameState={gameState}
            gameInStartedPhase={gameInStartedPhase}
            serverFinished={serverFinished}
            socket={socket}
            configSchema={configSchema}
            isHost={isHost}
            gameTypes={gameTypes}
            onChangeGameType={(key) => {
              if (roomId) {
                onChangeGameType(roomId, key);
              }
            }}
          />

      {/* Spectator Banner */}
      {isSpectator && (
        <div className="flex-1 justify-center mb-4">
          <div className="mt-1 dark:text-gray-300 text-gray-700 text-sm px-3 py-1 rounded bg-yellow-100 dark:bg-yellow-900 border border-yellow-300">
            You are spectating this room
          </div>
        </div>
      )}

          {gameInFinishedPhase && showPlayAgain && <GameRoomPlayAgain />}
          {/* PHASED RENDERING LOGIC */}
          {gameInConfigPhase &&
            !gameInCountdownPhase &&
            (() => {
              return <GameRoomConfig />;
            })()}
          {/* During countdown, render the game component (for non-turn-based games) and overlay the countdown UI */}
          {gameInCountdownPhase && !usesEmbeddedCountdownOverlay && (
            <div className="relative">
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="text-9xl font-extrabold text-white drop-shadow-lg select-none">
                  {countdownValue > 0 ? countdownValue : "GO!"}
                </div>
              </div>
            </div>
          )}
          {
            // show once started
            ((gameInStartedPhase &&
              !gameInConfigPhase &&
              !gameInCountdownPhase) ||
              (usesEmbeddedCountdownOverlay && serverCountdownActive) ||
              (showPlayAgain && gameInFinishedPhase)) &&
              gameComponent
          }
        </div>
      </div>
      {/* </section> */}
      {/* Right: Players/Chat */}
      <div className="w-full md:w-1/3 flex flex-col gap-6">
        {isSpectator && gameState?.finished && (
          <div className="text-xs text-gray-600 dark:text-gray-300">
            {isQueuedForNextRound
              ? "You are queued for the next round."
              : nextRoundSlotsRemaining > 0
                ? `${nextRoundSlotsRemaining} seat${nextRoundSlotsRemaining === 1 ? "" : "s"} open for the next round.`
                : "The next round is currently full."}
          </div>
        )}

                  {/* Display GameRoom name at the top */}

          {roomName && (
            <div className="flex items-center justify-between gap-3 font-bold mb-2 bg-zinc-200 dark:bg-zinc-900 rounded px-3 py-2 border border-gray-300 dark:border-gray-600">
              <div className="flex-1 basis-1/2 min-w-0 text-left text-md leading-snug" style={{ wordBreak: 'break-word' }}>
                {isEditingRoomName ? (
                  <input
                    ref={roomNameInputRef}
                    type="text"
                    value={draftRoomName}
                    onChange={(event) => setDraftRoomName(event.target.value)}
                    onBlur={saveRoomNameEdit}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        event.currentTarget.blur();
                      }
                      if (event.key === "Escape") {
                        event.preventDefault();
                        cancelRoomNameEdit();
                      }
                    }}
                    maxLength={20}
                    className="w-full bg-transparent text-blue-700 dark:text-blue-200 border-0 outline-none ring-0 focus:ring-0 p-0 m-0 font-bold"
                    style={{ boxShadow: 'none' }}
                    aria-label="Edit room name"
                  />
                ) : (
                  <button
                    type="button"
                    className={`w-full bg-transparent border-0 p-0 m-0 text-left text-blue-700 dark:text-blue-200 break-words leading-snug ${isHost ? 'cursor-text' : 'cursor-default'}`}
                    title={isHost ? `${roomName} (click to edit)` : roomName}
                    style={{ wordBreak: 'break-word' }}
                    onClick={beginRoomNameEdit}
                    disabled={!isHost}
                  >
                    {roomName}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 ml-2 flex-1 basis-1/2 justify-end">
                {isHost && (
                  <>
                    <button
                      title={gameRoomData.gameRoomDetails.passworded ? "Password Protected" : "No Password"}
                      className={`p-2 rounded-full border ${gameRoomData.gameRoomDetails.passworded ? 'bg-red-200 border-red-400 text-red-900' : 'bg-green-200 border-green-400 text-green-700'} hover:shadow`}
                      onClick={() => setShowPasswordModal(true)}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {gameRoomData.gameRoomDetails.passworded ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 11V7a5 5 0 00-10 0v4M5 11h14a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2z" /></svg>
                      ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 7a5 5 0 0110 0v4" />
                          <rect x="5" y="11" width="14" height="9" rx="2" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 7c0-2.761 2.239-5 5-5s5 2.239 5 5" />
                        </svg>
                      )}
                    </button>
                    <button
                      title={allowSpectators ? "Spectators allowed" : "Spectators disabled"}
                      className={`p-2 rounded-full border ${allowSpectators ? 'bg-sky-200 border-sky-400 text-sky-900' : 'bg-gray-200 border-gray-400 text-gray-700'} hover:shadow`}
                      onClick={handleToggleSpectatorsClick}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {allowSpectators ? (
                        // Eye open (spectators allowed)
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                          <ellipse cx="12" cy="12" rx="9.5" ry="6.5" stroke="currentColor" strokeWidth="2" fill="none" />
                          <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
                          <circle cx="12" cy="12" r="4.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                      ) : (
                        // Eye closed (spectators not allowed)
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                          <ellipse cx="12" cy="12" rx="9.5" ry="6.5" stroke="currentColor" strokeWidth="2" fill="none" />
                          <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
                          <circle cx="12" cy="12" r="4.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                          <line x1="5" y1="19" x2="19" y2="5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                      )}
                    </button>
                  </>
                )}
                <button
                  title={
                    discordInviteUrl
                      ? (isHost ? "Manage this room's Discord chat link" : "Join Discord Chat")
                      : (isHost ? "Set up a Discord chat for this room" : "The host hasn't set up a Discord chat yet")
                  }
                  className={`p-2 rounded-full border ${discordInviteUrl ? 'bg-indigo-200 border-indigo-400 text-indigo-900 hover:shadow discord-link-pulse' : 'bg-gray-200 border-gray-400 text-gray-500'} ${(!discordInviteUrl && !isHost) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!discordInviteUrl && !isHost}
                  onClick={() => {
                    if (isHost) {
                      setDiscordLinkInput(discordInviteUrl || "");
                      setShowDiscordModal(true);
                      return;
                    }
                    if (discordInviteUrl) {
                      window.open(discordInviteUrl, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {/* Discord icon */}
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.865-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.6 12.6 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037a19.74 19.74 0 0 0-4.885 1.515a.07.07 0 0 0-.032.027C.533 9.045-.32 13.579.099 18.057a.082.082 0 0 0 .031.056a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106a13.1 13.1 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .079.01c.12.099.246.197.373.291a.077.077 0 0 1-.006.128a12.3 12.3 0 0 1-1.873.892a.077.077 0 0 0-.041.106c.36.699.772 1.364 1.225 1.993a.076.076 0 0 0 .084.029a19.84 19.84 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 0 0-.031-.028M8.02 15.331c-1.183 0-2.157-1.086-2.157-2.42s.955-2.42 2.157-2.42c1.211 0 2.176 1.096 2.157 2.42c0 1.334-.955 2.42-2.157 2.42m7.975 0c-1.183 0-2.157-1.086-2.157-2.42s.955-2.42 2.157-2.42c1.211 0 2.176 1.096 2.157 2.42c0 1.334-.946 2.42-2.157 2.42" />
                  </svg>
                </button>
                <button
                  title="Leave Room"
                  className={`p-2 rounded-full border bg-rose-100 border-rose-400 text-rose-900 hover:shadow hover:bg-rose-200`}
                  onClick={handleLeaveClick}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {/* Door exit icon */}
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="3" y="3" width="13" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
                    <circle cx="7.5" cy="12" r="1" fill="currentColor" />
                    <path d="M16 17l5-5-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          )}


        

        {/* Sit Out button for players */}
        {/* {!isSpectator && !isHost && (
          <button
            className="px-3 py-1 bg-yellow-500 text-white rounded"
            onClick={() => {
              onBecomeSpectator(roomId);
            }}
          >
            Sit Out (Become Spectator)
          </button>
        )} */}

        <div>
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
            Players ({gamePlayers.length})
          </h2>
          <GameRoomPlayers
            voiceChatUsers={voiceChatUsers}
            speakingUsers={speakingUsers}
            onParticipantSelect={setSelectedParticipant}
          />
        </div>
        {allowSpectators && (
          <div>
            <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
              Spectators ({gameRoomData?.gameRoomSpectators?.length || 0})
            </h2>
            <GameRoomSpectators
              isConfigVisible={isConfigVisible}
              onParticipantSelect={setSelectedParticipant}
            />
          </div>
        )}
        {/* <div>
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
            Game Chat
          </h2>
          <GameRoomChat
            roomId={roomId}
            messages={messages}
            sendMessage={sendMessage}
            user={user}
            handleGameRoomTyping={handleGameRoomTyping}
            chatInput={chatInput}
            setChatInput={setChatInput}
            handleSend={handleSend}
            handleInputChange={handleInputChange}
            gameRoomTypingPlayers={gameRoomTypingPlayers}
            handleInputBlur={handleInputBlur}
            onVisibilityChange={setIsChatVisible}
            //inputClassName="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-800 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-100 dark:focus:ring-blue-500 dark:focus:border-blue-500"
          />
          <GameRoomVoiceChat
            socket={socket}
            socketId={socket.id}
            voiceChatUsers={voiceChatUsers}
            setVoiceChatUsers={setVoiceChatUsers}
            setSpeakingUsers={setSpeakingUsers}
            isSpectator={isSpectator}
          />
        </div> */}
      </div>

      {showConfirm && (
        <Modal show={showConfirm} title="Leave Game" onClose={cancelLeave}>
          <div className="w-full max-w-md mx-auto p-4 bg-white dark:bg-gray-800 text-center">
            <p className="mb-4 text-lg dark:text-gray-100">
              Are you sure you want to leave the game room?
            </p>
            <div className="flex justify-center mt-4">
              <button
                className="bg-red-500 text-white p-2 rounded mr-2"
                onClick={confirmLeave}
              >
                Yes, Leave
              </button>
              <button
                className="bg-gray-300 p-2 rounded dark:bg-gray-600 dark:text-gray-100"
                onClick={cancelLeave}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showDisableSpectatorsConfirm && (
        <Modal show={showDisableSpectatorsConfirm} title="Disable Spectators" onClose={cancelDisableSpectators}>
          <div className="w-full max-w-md mx-auto p-4 bg-white dark:bg-gray-800 text-center">
            <p className="mb-4 text-lg dark:text-gray-100">
              {spectatorCount === 1
                ? "There is currently 1 spectator in the room. If you continue, that spectator will be kicked from the room."
                : `There are currently ${spectatorCount} spectators in the room. If you continue, all of them will be kicked from the room.`}
            </p>
            <div className="flex justify-center mt-4 gap-2">
              <button
                className="bg-rose-600 text-white p-2 rounded hover:bg-rose-700"
                onClick={confirmDisableSpectators}
              >
                Continue
              </button>
              <button
                className="bg-gray-300 p-2 rounded dark:bg-gray-600 dark:text-gray-100"
                onClick={cancelDisableSpectators}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {selectedParticipant && (
        <Modal
          show={!!selectedParticipant}
          title={selectedParticipant.username || "Participant"}
          onClose={() => setSelectedParticipant(null)}
        >
          <div className="w-full max-w-md mx-auto p-4 bg-white dark:bg-gray-800">
            <div className="flex items-center gap-4 mb-4">
              <span
                className="flex h-12 w-12 items-center justify-center rounded-full border text-2xl"
                style={{
                  backgroundColor: selectedParticipant.color || "#e57373",
                  borderColor: selectedParticipant.color || "#e57373",
                }}
              >
                {selectedParticipant.icon || (selectedParticipant.role === "spectator" ? "👀" : "😀")}
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900 dark:text-gray-100">
                  {selectedParticipant.username}
                  {selectedParticipantIsCurrentUser ? " (You)" : ""}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                  {selectedParticipant.role}
                  {selectedParticipantIsHost ? " • Host" : ""}
                </div>
              </div>
              {selectedParticipant.flag ? (
                <img
                  src={`/flags/${selectedParticipant.flag}.png`}
                  alt={FLAG_LABELS[selectedParticipant.flag] || selectedParticipant.flag.toUpperCase()}
                  onError={(event) => {
                    try {
                      event.target.onerror = null;
                      event.target.src = `/flags/${selectedParticipant.flag}.svg`;
                    } catch (e) {}
                  }}
                  className="h-10 w-14 object-contain"
                />
              ) : (
                <div className="rounded border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
                  No flag
                </div>
              )}
            </div>

            <div className="space-y-2">
              {canMakeSelectedParticipantHost && (
                <button
                  type="button"
                  className="w-full rounded bg-yellow-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-yellow-600"
                  onClick={() => handleParticipantAction("makeHost")}
                  title="Make this player the new host"
                >
                  Make Player new Host of room
                </button>
              )}
              {canMoveSelectedParticipantToPlayer && (
                <button
                  type="button"
                  className="w-full rounded bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700"
                  onClick={() => handleParticipantAction("makePlayer")}
                  title="Promote to player"
                >
                  Promote to Player
                </button>
              )}
              {canMoveSelectedParticipantToSpectator && (
                <button
                  type="button"
                  className="w-full rounded bg-slate-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  onClick={() => handleParticipantAction("makeSpectator")}
                  title="Move to spectators"
                >
                  Make Spectator
                </button>
              )}
              {canKickSelectedParticipant && (
                <button
                  type="button"
                  className="w-full rounded bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
                  onClick={() => handleParticipantAction("kick")}
                  title="Remove from room"
                >
                  Kick from Room
                </button>
              )}
            </div>

            {/* {!isHost && (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                Only the host can use these actions. You can still view what controls are available for this participant.
              </p>
            )} */}
          </div>
        </Modal>
      )}

            {/* Password Modal for host */}
      {isHost && (
        <Modal show={showPasswordModal} onClose={() => setShowPasswordModal(false)} title={gameRoomData?.gameRoomDetails?.passworded ? "Change Room Password" : "Set Room Password"}>
          <div className="w-full max-w-md mx-auto p-4 bg-white dark:bg-gray-800 text-center">
          <form
            onSubmit={e => {
              e.preventDefault();
              if (passwordInput !== undefined) {
                socket.emit('setRoomPassword', { roomId, password: passwordInput });
                setPasswordInput("");
                setShowPasswordModal(false);
              }
            }}
            className="flex flex-col gap-3"
          >
            <input
              type="text"
              value={passwordInput || ""}
              onChange={e => setPasswordInput(e.target.value)}
              placeholder="Enter Password"
              className="border border-gray-400 rounded px-2 py-1 text-base text-gray-800 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 rounded bg-blue-500 text-white font-semibold hover:bg-blue-600">Set</button>
              <button type="button" className="px-4 py-2 rounded bg-gray-300 text-gray-800 font-semibold hover:bg-gray-400" onClick={() => { setPasswordInput(""); socket.emit('setRoomPassword', { roomId, password: "" }); setShowPasswordModal(false); }}>Remove</button>
            </div>
          </form>
          </div>
        </Modal>
      )}

      {/* Discord Chat Modal for host */}
      {isHost && (
        <Modal show={showDiscordModal} onClose={() => setShowDiscordModal(false)} title={discordInviteUrl ? "Update Discord Chat Link" : "Set Up Discord Chat"}>
          <div className="w-full max-w-md mx-auto p-4 bg-white dark:bg-gray-800 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 text-left">
              Don't have a server yet?{" "}
              <a
                href="https://discord.new"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-300 underline"
              >
                Create one on Discord
              </a>
              , then paste an invite link below so everyone in this room can join.
            </p>
            {discordInviteUrl && (
              <p className="text-sm mb-3 text-left">
                <a
                  href={discordInviteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 dark:text-indigo-300 underline"
                >
                  Open the current Discord invite ↗
                </a>
              </p>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                socket.emit("setRoomDiscordLink", { roomId, discordUrl: discordLinkInput }, (response) => {
                  if (response?.ok) {
                    setShowDiscordModal(false);
                    return;
                  }
                  if (typeof addToast === "function") {
                    addToast(response?.error || "Failed to save Discord link", { type: "error", duration: 2500 });
                  }
                });
              }}
              className="flex flex-col gap-3"
            >
              <input
                type="url"
                value={discordLinkInput}
                onChange={(e) => setDiscordLinkInput(e.target.value)}
                placeholder="https://discord.gg/yourcode"
                className="border border-gray-400 rounded px-2 py-1 text-base text-gray-800 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 rounded bg-blue-500 text-white font-semibold hover:bg-blue-600">Save</button>
                {discordInviteUrl && (
                  <button
                    type="button"
                    className="px-4 py-2 rounded bg-gray-300 text-gray-800 font-semibold hover:bg-gray-400"
                    onClick={() => {
                      socket.emit("setRoomDiscordLink", { roomId, discordUrl: "" }, () => {});
                      setDiscordLinkInput("");
                      setShowDiscordModal(false);
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </form>
          </div>
        </Modal>
      )}

    </>
  );
}

export default GameRoom;
